import { FC, useCallback, useEffect, useState } from 'react'
import { SubscriptionPeriod, SubscriptionPlanType } from 'models/user/userWorkspaceDTOs'

import { ChargebeeModal } from 'components/common/ChargebeeModal'
import { ChoosePlan } from 'components/common/ChoosePlan'
import { MUIButton } from 'components/common/MUIButton'
import { Path } from 'constants/router'
import { PlanSummaryCard } from './PlanSummaryCard.component'
import { Stack } from '@mui/material'
import { UpgradeSeatConfirmationModal } from './UpgradeSeatConfirmationModal.component'
import { SELF_SERVE_SUBSCRIPTION_PLAN_TYPES, SETTLED_SUBSCRIPTION_STATUSES } from 'constants/subscription'
import { getAccessLink } from 'utils/localization/getAccessLink'
import i18n from 'translations/i18n'
import { useLocation, useNavigate } from 'react-router'
import { useSubscriptionCheckout } from 'utils/hooks/useSubscriptionCheckout'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { workspacePlanType } from 'constants/workspaces/workspaceMemberLimit'

export const PlanContentModule: FC = () => {
  const { t } = useTranslation(['workspace_settings'])
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUserWorkspace, currentUserWorkspaceMembership } = useUserData()

  const {
    isCheckoutLoading,
    isCheckoutOpen,
    checkoutUrl,
    openCheckout,
    closeCheckout,
  } = useSubscriptionCheckout(currentUserWorkspace?.id || '')

  const planType = currentUserWorkspace
    ? workspacePlanType[currentUserWorkspace.plan]
    : SubscriptionPlanType.PERSONAL

  const isPersonalPlan = planType === SubscriptionPlanType.PERSONAL

  // Falls back to the workspace's own status (/user/me) and treats an absent one as unsettled: the
  // membership comes from a query that can fail, and an unknown status must not read as "nothing to
  // renew here".
  const workspaceStatus = currentUserWorkspaceMembership?.workspaceStatus ?? currentUserWorkspace?.status
  const isSubscriptionSettled = !!workspaceStatus
    && SETTLED_SUBSCRIPTION_STATUSES.includes(workspaceStatus)
    && !currentUserWorkspaceMembership?.terminatedAt

  // Only someone redirected here from bkbn.com/pricing came to buy, and only a self-served plan can
  // be bought again by opening another workspace — this is not an upsell for everyone who opens
  // their billing page. A subscription that is failing or ending wants renewing, not a second one
  // beside it (QA-246).
  const offersAnotherSubscription = !!(location.state as { fromPricingRedirect?: boolean } | null)?.fromPricingRedirect
    && SELF_SERVE_SUBSCRIPTION_PLAN_TYPES.includes(planType)
    && isSubscriptionSettled

  // A free→paid upgrade converts every admin to a paid FULL seat — the seat/cost confirmation
  // must run before the checkout mutation, because its success is what opens Chargebee.
  const [pendingUpgradePeriod, setPendingUpgradePeriod] = useState<SubscriptionPeriod | null>(null)

  // The confirmation stays open (with its Confirm disabled) while the checkout link is fetched,
  // and closes only when Chargebee actually opens — a failed fetch keeps it up next to the toast.
  useEffect(() => {
    if (isCheckoutOpen) setPendingUpgradePeriod(null)
  }, [isCheckoutOpen])

  const handleSelectPlan = useCallback((selectedPlan: SubscriptionPlanType, period: SubscriptionPeriod) => {
    if (selectedPlan === SubscriptionPlanType.ENTERPRISE) {
      window.open(getAccessLink(i18n.language), '_blank', 'noopener noreferrer')
      return
    }
    if (selectedPlan === SubscriptionPlanType.PRO) {
      if (isPersonalPlan && currentUserWorkspace) {
        setPendingUpgradePeriod(period)
        return
      }
      openCheckout(period)
    }
  }, [openCheckout, isPersonalPlan, currentUserWorkspace])

  return (
    <>
      <Stack gap="1.6rem" padding="5.6rem 0">
        {isPersonalPlan && (
          <ChoosePlan
            currentPlan={planType}
            onSelectPlan={handleSelectPlan}
            showSubtitle
          />
        )}

        {!isPersonalPlan && (
          <>
            <PlanSummaryCard planType={planType} onSelectPlan={handleSelectPlan} />

            {/* A paid workspace has nothing left to sell the visitor — a second subscription needs
                a second workspace. */}
            {offersAnotherSubscription && (
              <Stack alignItems="center">
                <MUIButton type="secondaryNoBorder" onClick={() => navigate(Path.WORKSPACE_CREATION)}>
                  {t('workspace_settings:plan.create_another_workspace')}
                </MUIButton>
              </Stack>
            )}
          </>
        )}
      </Stack>

      {!!pendingUpgradePeriod && !!currentUserWorkspace && (
        <UpgradeSeatConfirmationModal
          isOpen={!!pendingUpgradePeriod}
          workspaceId={currentUserWorkspace.id}
          period={pendingUpgradePeriod}
          isLoading={isCheckoutLoading}
          onConfirm={() => openCheckout(pendingUpgradePeriod)}
          onClose={() => setPendingUpgradePeriod(null)}
        />
      )}

      <ChargebeeModal
        title={t('workspace_settings:plan.subscription_checkout')}
        hostedPageUrl={checkoutUrl}
        isOpen={isCheckoutOpen}
        handleClose={closeCheckout}
        isLoading={isCheckoutLoading}
      />
    </>
  )
}
